import { EmployeesMap } from '@/hooks/useEmployeesMap';
import { Asset, AssetAssignmentEmployee } from '@/types/assets';

export type AssigneeResolverArgs = {
  isDivisionsEnabled: boolean;
} & Pick<EmployeesMap, 'getEmployeeById'>;

/**
 * Only the id is read from `assignedToEmployee`: the backend is expected to
 * trim that payload to `{ id }`, so everything else comes from the employees map.
 */
export const getAssigneeId = (asset: Asset) =>
  asset.assignedTo ?? asset.assignedToEmployee?.id;

/**
 * Resolves the assignee's details. With divisions on they come from the
 * division-scoped employees map, and undefined means the user can't see them.
 * With divisions off the map doesn't exist, so the payload is used as before.
 */
export const getAssignee = (
  asset: Asset,
  { isDivisionsEnabled, getEmployeeById }: AssigneeResolverArgs
): AssetAssignmentEmployee | undefined =>
  isDivisionsEnabled
    ? getEmployeeById(getAssigneeId(asset))
    : asset.assignedToEmployee;

/**
 * An assignee is hidden when divisions are on and the asset points at an
 * employee that the division-scoped employees map doesn't contain, i.e. the
 * user can't see them. Users who can't list employees get an empty map, so
 * every assignee is hidden for them.
 */
export const isAssigneeHidden = (
  asset: Asset,
  { isDivisionsEnabled, getEmployeeById }: AssigneeResolverArgs
) =>
  isDivisionsEnabled &&
  !!getAssigneeId(asset) &&
  !getEmployeeById(getAssigneeId(asset));
